import { ref, computed } from 'vue';

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

export interface User {
  id: number;
  nom: string;
  email: string;
  role: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  refreshToken?: string;
}

// État global partagé (singleton)
const token = ref<string | null>(localStorage.getItem('auth_token'));
const storedUser = localStorage.getItem('auth_user');
const user = ref<User | null>(storedUser ? JSON.parse(storedUser) : null);
const storedRefreshToken = localStorage.getItem('refresh_token');
const refreshToken = ref<string | null>(storedRefreshToken);
const loading = ref(false);
const error = ref<string | null>(null);

export function useAuth() {
  const isAuthenticated = computed(() => !!token.value && !!user.value);

  async function login(credentials: LoginCredentials): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${API_BASE}/authentification/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies for CORS
        body: JSON.stringify(credentials)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Erreur de connexion');
      }

      const data: AuthResponse = await response.json();
      
      token.value = data.token;
      user.value = data.user;
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      if (data.refreshToken) {
        refreshToken.value = data.refreshToken;
        localStorage.setItem('refresh_token', data.refreshToken);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur de connexion';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    const rt = refreshToken.value;
    token.value = null;
    user.value = null;
    refreshToken.value = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('refresh_token');
    // Fire-and-forget: revoke server-side
    if (rt) {
      fetch(`${API_BASE}/authentification/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt })
      }).catch(() => {});
    }
    // Redirection sera gérée par le composant qui appelle logout
  }

  async function fetchProfile(): Promise<void> {
    if (!token.value) return;

    try {
      const response = await fetch(`${API_BASE}/authentification/me`, {
        headers: {
          Authorization: `Bearer ${token.value}`
        }
      });

      if (response.ok) {
        const profile = await response.json();
        user.value = {
          id: profile.id,
          nom: profile.nom,
          email: profile.email,
          role: profile.role
        };
        localStorage.setItem('auth_user', JSON.stringify(user.value));
      } else if (response.status === 401) {
        // Token invalide, déconnecter
        await logout();
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du profil:', err);
    }
  }

  // Restaurer l'utilisateur depuis localStorage au démarrage
  function restoreSession(): void {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      try {
        token.value = storedToken;
        user.value = JSON.parse(storedUser);
        // Vérifier que le token est toujours valide
        fetchProfile();
      } catch (err) {
        console.error('Erreur lors de la restauration de la session:', err);
        logout();
      }
    }
  }

  function getAuthHeaders(): Record<string, string> {
    if (!token.value) return {};
    return {
      Authorization: `Bearer ${token.value}`
    };
  }

  return {
    token: computed(() => token.value),
    user: computed(() => user.value),
    refreshToken: computed(() => refreshToken.value),
    isAuthenticated,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    login,
    logout,
    fetchProfile,
    restoreSession,
    getAuthHeaders
  };
}

